import { generateText } from "@tiptap/core";
import type { JSONContent } from "@tiptap/react";

import { createEditorExtensions } from "@/shared/components/editor/lib/create-editor-extensions";

import type { TextContentMode } from "./types";

export const MARKDOWN_MIME_TYPE = "text/markdown";
export const TIPTAP_JSON_MIME_TYPE = "application/vnd.tiptap+json";

const ATTACHMENT_URI_PREFIX = "attachment://";
const TEXT_NOTE_EXTENSIONS = createEditorExtensions({
  contentMode: "tiptap-json",
});

function getMimeEssence(mimeType?: string) {
  return mimeType?.split(";", 1)[0]?.trim().toLowerCase();
}

export function getTextContentMode(mimeType?: string): TextContentMode {
  if (!mimeType) {
    return "tiptap-json";
  }

  const mimeEssence = getMimeEssence(mimeType);

  if (mimeEssence === MARKDOWN_MIME_TYPE) {
    return "markdown";
  }

  if (mimeEssence === TIPTAP_JSON_MIME_TYPE) {
    return "tiptap-json";
  }

  throw new Error(`不支持的文本内容类型：${mimeType}`);
}

export function isMarkdownTextContent(mimeType?: string) {
  return getMimeEssence(mimeType) === MARKDOWN_MIME_TYPE;
}

export function resolveInitialTextContentMode(
  mimeType?: string,
  requestedMode: TextContentMode = "tiptap-json",
) {
  return mimeType ? getTextContentMode(mimeType) : requestedMode;
}

export function getTextContentMimeType(mode: TextContentMode) {
  return mode === "markdown" ? MARKDOWN_MIME_TYPE : TIPTAP_JSON_MIME_TYPE;
}

function sanitizeTextFileName(title?: string) {
  return (title?.trim() || `text-${Date.now()}`)
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 50) || `text-${Date.now()}`;
}

export function buildTextContentFileName(title: string | undefined, mode: TextContentMode) {
  return `${sanitizeTextFileName(title)}.${mode === "markdown" ? "md" : "json"}`;
}

function isJsonContent(value: unknown): value is JSONContent {
  return (
    !!value &&
    typeof value === "object" &&
    "type" in value &&
    (value as { type?: unknown }).type === "doc"
  );
}

export function parseTiptapJson(rawContent: string): JSONContent {
  const parsedContent: unknown = JSON.parse(rawContent);

  if (!isJsonContent(parsedContent)) {
    throw new Error("无效的 Tiptap JSON 文档");
  }

  return parsedContent;
}

export function getTextNotePlainText(note?: string) {
  if (!note?.trim()) {
    return "";
  }

  try {
    return generateText(
      parseTiptapJson(note),
      TEXT_NOTE_EXTENSIONS,
      { blockSeparator: "\n" },
    ).trim();
  } catch {
    return note;
  }
}

export function buildAttachmentUri(attachmentId: string) {
  return `${ATTACHMENT_URI_PREFIX}${attachmentId}`;
}

export function parseAttachmentUri(value: unknown) {
  if (typeof value !== "string" || !value.startsWith(ATTACHMENT_URI_PREFIX)) {
    return null;
  }

  const attachmentId = value.slice(ATTACHMENT_URI_PREFIX.length);
  return attachmentId || null;
}

function isRemoteImageUrl(value: string) {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function transformImageSources(
  content: JSONContent,
  transform: (src: string) => string | undefined,
): JSONContent {
  const visitNode = (node: JSONContent): JSONContent => {
    let nextNode = node;

    if (node.type === "image" && typeof node.attrs?.src === "string") {
      const nextSource = transform(node.attrs.src);

      if (nextSource && nextSource !== node.attrs.src) {
        nextNode = {
          ...node,
          attrs: {
            ...node.attrs,
            src: nextSource,
          },
        };
      }
    }

    if (!node.content?.length) {
      return nextNode;
    }

    return {
      ...nextNode,
      content: node.content.map(visitNode),
    };
  };

  return visitNode(content);
}

export function hydrateAttachmentUrls(
  content: JSONContent,
  attachmentUrlMap: ReadonlyMap<string, string>,
) {
  return transformImageSources(content, (src) => {
    const attachmentId = parseAttachmentUri(src);
    return attachmentId ? attachmentUrlMap.get(attachmentId) : undefined;
  });
}

export function dehydrateAttachmentUrls(
  content: JSONContent,
  attachmentIdMap: ReadonlyMap<string, string>,
) {
  return transformImageSources(content, (src) => {
    if (parseAttachmentUri(src)) {
      return src;
    }

    const attachmentId = attachmentIdMap.get(src);

    if (attachmentId) {
      return buildAttachmentUri(attachmentId);
    }

    if (isRemoteImageUrl(src)) {
      return src;
    }

    throw new Error("本地图片必须先上传为笔记附件");
  });
}

export function selectMarkdownSource({
  edited,
  normalized,
  original,
}: {
  edited: boolean;
  normalized: string;
  original: string;
}) {
  return edited ? normalized : original;
}
